import { QueryClient, UseMutationOptions, UseQueryOptions, useQuery } from '@tanstack/react-query';
import { isTotalsRebuildRequired } from 'src/queries/Item/isTotalsRebuildRequired';
import { Item } from 'src/types';
import { filterArrayIndexes, getArrayEqualChunks } from 'src/util/arrayUtils';
import { PromiseQueue } from 'src/util/promiseQueue';
import { queryClient } from '..';
import { createScopedMutation } from '../createScopedMutation';
import { DELETE_KEY, ITEM_QUERY_KEY, LABEL_POSTPROCESSING_QUERY_KEY, LIST_KEY, POST_KEY, PUT_KEY } from '../queryKeys';
import {
  invalidateIfAllMutationsDone,
  JSON_MUTATION_TIMEOUT_MS,
  queryYemboApiCall,
  queryYemboApiCallWithReturnValue,
  YemboApiError,
} from '../queryYemboApiCalls';
import { useIsReadyForFetch } from '../useIsReadyForFetch';
import { clearItemsLoadProgress, setItemsLoadProgress } from './loadProgress';
import { collectItemPages, type ItemPageResponse } from './pagination';

const url = 'item';

const MAX_CHUNK_SIZE = 100;
const MAX_CONCURRENT_REQUESTS_COUNT = 3;

export type PartialItem = Partial<Item> & Pick<Item, 'key'>;

export type UseTagsOptions = Partial<UseQueryOptions<Item[], unknown>>;

/** How long any one item page request may take. */
const ITEM_PAGE_TIMEOUT_MS = 30000;

const getItemsPage = (jobKey: string, page: number): Promise<ItemPageResponse | null> =>
  queryYemboApiCallWithReturnValue<ItemPageResponse>({
    url,
    urlParams: new URLSearchParams({
      jobKey,
      // comparables feed the replacement strip in the review drawer; revisions
      // feed the "was X, now Y" strikethrough and the change log. Without these
      // the client has no way to know an item was edited.
      children: 'comparable,revision',
      page: String(page),
    }),
    shouldNotifyAndThrowError: true,
    timeoutMs: ITEM_PAGE_TIMEOUT_MS,
  });

export const itemsListQueryKey = (jobKey: string) => [ITEM_QUERY_KEY, LIST_KEY, jobKey];

/**
 * Every item on the job; see collectItemPages for why this is paged.
 *
 * On a first load the pages are streamed into the cache as they arrive, so the
 * review screen renders once the first 50 items are in rather than sitting on a
 * skeleton until the last page of a large job lands.
 */
const getItems = async (jobKey: string): Promise<Item[]> => {
  const listKey = itemsListQueryKey(jobKey);

  // Only stream on a first load. A refetch -- which is what every item mutation
  // triggers -- already has the whole list on screen, and replacing it with
  // page 1 would make the inventory visibly collapse and refill each time.
  const isFirstLoad = queryClient.getQueryData<Item[]>(listKey) === undefined;

  try {
    return await collectItemPages(
      (page) => getItemsPage(jobKey, page),
      isFirstLoad
        ? (partialItems, progress) => {
            queryClient.setQueryData<Item[]>(listKey, partialItems);
            setItemsLoadProgress(jobKey, progress);
          }
        : undefined
    );
  } catch (error) {
    // A page failed after some had already been shown. Drop the partial list
    // rather than leaving a reviewer looking at part of a job that reads as
    // the whole of it; react-query retries, and the skeleton returns meanwhile.
    if (isFirstLoad) queryClient.setQueryData<Item[] | undefined>(listKey, undefined);
    throw error;
  } finally {
    clearItemsLoadProgress(jobKey);
  }
};

/**
 * Every item on the job, fetched page by page without touching the cache: the
 * items as they are now, with their photo URLs signed now. What a report that
 * downloads the item photos reads, since the cached list's URLs expire.
 */
export const fetchJobItems = (jobKey: string): Promise<Item[]> =>
  collectItemPages((page) => getItemsPage(jobKey, page));

type ChunkedRequestResponses<T, R> = {
  chunks: T[][];
  settledResponses: PromiseSettledResult<R>[];
};

class ChunkedRequestError<T, R> extends Error {
  constructor(
    message: string,
    public chunks: T[][],
    public settledResponses: PromiseSettledResult<R>[],
    options?: ErrorOptions
  ) {
    super(message, options);
  }
}

function isPartialTagChunks(list: unknown[][]): list is PartialItem[][] {
  if (!Array.isArray(list) || !list.length) return false;

  const [chunk] = list;
  if (!Array.isArray(chunk) || !chunk.length) return false;

  const [item] = list;
  return !!item && typeof item === 'object' && 'key' in item && typeof item.key === 'string';
}

async function runChunkedRequest<T, R>(
  list: T[],
  apiRequest: (chunk: T[]) => Promise<R>,
  maxChunkSize = MAX_CHUNK_SIZE,
  maxConcurrentRequests = MAX_CONCURRENT_REQUESTS_COUNT,
  errorMessage?: string
): Promise<ChunkedRequestResponses<T, R>> {
  const chunks = getArrayEqualChunks(list, maxChunkSize);
  const promiseQueue = new PromiseQueue<R>({
    maxConcurrent: maxConcurrentRequests,
  });

  const promises = promiseQueue.enqueue(
    chunks.map((chunk) => () => apiRequest(chunk)),
    60000
  );

  const settledResponses = await Promise.allSettled(promises);
  if (settledResponses.some((r) => r.status === 'rejected')) {
    throw new ChunkedRequestError(
      errorMessage ?? 'At least one of the chunked requests has been failed.',
      chunks,
      settledResponses
    );
  }

  return { chunks, settledResponses };
}

function getRejectedResponsesTags<T extends { key: string }>(responses: ChunkedRequestResponses<T, unknown>): T[] {
  const { chunks: tagsChunks, settledResponses } = responses;
  const rejectedRequestIndexes = filterArrayIndexes(settledResponses, (r) => r.status === 'rejected');
  return rejectedRequestIndexes.flatMap((index) => tagsChunks[index] ?? []);
}

export const postItems = (items: Item[]) =>
  queryYemboApiCall({
    url: `${url}/batch`,
    method: 'POST',
    body: JSON.stringify(items),
    urlParams: new URLSearchParams({ shouldRebuild: 'true' }),
    shouldNotifyAndThrowError: true,
    timeoutMs: JSON_MUTATION_TIMEOUT_MS,
  });

export const postTagsByChunks = (items: Item[]): Promise<ChunkedRequestResponses<Item, Response>> => {
  return runChunkedRequest(items, postItems, undefined, undefined, 'Failed to create items');
};

export const putItems = (items: PartialItem[]) =>
  queryYemboApiCall({
    url: `${url}/batch`,
    method: 'PUT',
    body: JSON.stringify(items),
    urlParams: new URLSearchParams({
      shouldRebuild: isTotalsRebuildRequired(items) ? 'true' : 'false',
    }),
    shouldNotifyAndThrowError: true,
    timeoutMs: JSON_MUTATION_TIMEOUT_MS,
  });

export const putTagsByChunks = (items: PartialItem[]): Promise<ChunkedRequestResponses<PartialItem, Response>> => {
  return runChunkedRequest(items, putItems, undefined, undefined, 'Failed to update items');
};

export const deleteItems = async (items: Pick<Item, 'key'>[]) => {
  if (items.length === 0) return true;

  return await queryYemboApiCall({
    url: `${url}/batch`,
    method: 'DELETE',
    body: JSON.stringify({
      keys: items.map(({ key }) => key),
    }),
    urlParams: new URLSearchParams({ shouldRebuild: 'true' }),
    shouldNotifyAndThrowError: true,
    timeoutMs: JSON_MUTATION_TIMEOUT_MS,
  });
};

export const deleteTagsByChunks = (
  items: Pick<Item, 'key'>[]
): Promise<ChunkedRequestResponses<PartialItem, Response | true>> => {
  return runChunkedRequest(items, deleteItems, undefined, undefined, 'Failed to delete items');
};

const useItemsQuery = (jobKey: string | undefined, options?: UseTagsOptions) =>
  useQuery({
    queryKey: [ITEM_QUERY_KEY, LIST_KEY, jobKey],
    queryFn: () => getItems(jobKey ?? ''),
    ...options,
    enabled: !!jobKey && options?.enabled !== false,
  });

export const useItemsQueryForOffline = (jobKey: string | undefined, options?: UseTagsOptions) =>
  useItemsQuery(jobKey, {
    ...options,
    enabled: useIsReadyForFetch() && options?.enabled !== false,
  });

type PostTagsOptions = UseMutationOptions<ChunkedRequestResponses<PartialItem, Response>, unknown, Item[], void>;

const getPostTagsOptions = (
  queryClient: QueryClient,
  roomKey: string,
  jobKey: string,
  options?: PostTagsOptions
): PostTagsOptions => {
  const mutationKey = [ITEM_QUERY_KEY, jobKey, POST_KEY];
  const tagsQueryKey = [ITEM_QUERY_KEY, LIST_KEY, jobKey];

  return {
    mutationKey,
    scope: { id: roomKey },
    mutationFn: postTagsByChunks,
    ...options,
    onMutate: async (items, context) => {
      await queryClient.cancelQueries({ queryKey: tagsQueryKey });

      queryClient.setQueryData<Item[]>(tagsQueryKey, (previousTags) =>
        previousTags ? [...previousTags, ...items] : undefined
      );

      options?.onMutate?.(items, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      if (error instanceof ChunkedRequestError) {
        const { settledResponses, chunks } = error;

        if (isPartialTagChunks(chunks)) {
          const rejectedTags = getRejectedResponsesTags({
            settledResponses,
            chunks,
          });

          queryClient.setQueryData<Item[]>(tagsQueryKey, (previousTags) =>
            previousTags?.filter((previousTag) => !rejectedTags.some(({ key }) => key === previousTag.key))
          );
        }
      }

      options?.onError?.(error, variables, onMutateResult, context);
    },
    onSettled: (responses, error, variables, onMutateResult, context) => {
      invalidateIfAllMutationsDone(tagsQueryKey, [ITEM_QUERY_KEY, jobKey]);
      options?.onSettled?.(responses, error, variables, onMutateResult, context);
    },
  };
};

export const { useScopedMutation: usePostItems, useScopedMutationBuilder: usePostItemsBuilder } =
  createScopedMutation(getPostTagsOptions);

type PutTagsOptions = UseMutationOptions<ChunkedRequestResponses<PartialItem, Response>, unknown, PartialItem[], void>;

const getPutTagsOptions = (
  queryClient: QueryClient,
  roomKey: string,
  jobKey: string,
  options?: PutTagsOptions
): PutTagsOptions => {
  const mutationKey = [ITEM_QUERY_KEY, jobKey, PUT_KEY];
  const tagsQueryKey = [ITEM_QUERY_KEY, LIST_KEY, jobKey];

  return {
    mutationKey,
    scope: { id: roomKey },
    mutationFn: (items) => {
      const currentTags = queryClient.getQueryData<Item[]>(tagsQueryKey);

      const existingTags = items.filter((item) => currentTags?.some((t) => t.key === item.key));

      return putTagsByChunks(existingTags);
    },
    ...options,
    onMutate: async (items, context) => {
      await queryClient.cancelQueries({ queryKey: tagsQueryKey });

      queryClient.setQueryData<Item[]>(tagsQueryKey, (previousTags) =>
        previousTags?.map((previousTag) => {
          const newTag = items.find(({ key }) => key === previousTag.key);
          return newTag ? { ...previousTag, ...newTag } : previousTag;
        })
      );

      options?.onMutate?.(items, context);
    },
    onSettled: (response, error, variables, onMutateResult, context) => {
      invalidateIfAllMutationsDone(tagsQueryKey, [ITEM_QUERY_KEY, jobKey]);

      options?.onSettled?.(response, error, variables, onMutateResult, context);
    },
  };
};

export const { useScopedMutation: usePutItems, useScopedMutationBuilder: usePutItemsBuilder } =
  createScopedMutation(getPutTagsOptions);

type DeleteTagsOptions = UseMutationOptions<
  ChunkedRequestResponses<PartialItem, Response | true>,
  unknown,
  Pick<Item, 'key'>[],
  void
>;

const getDeleteTagsOptions = (
  queryClient: QueryClient,
  roomKey: string,
  jobKey: string,
  options?: DeleteTagsOptions
): DeleteTagsOptions => {
  const mutationKey = [ITEM_QUERY_KEY, jobKey, DELETE_KEY];
  const tagsQueryKey = [ITEM_QUERY_KEY, LIST_KEY, jobKey];

  return {
    mutationKey,
    scope: { id: roomKey },
    mutationFn: deleteTagsByChunks,
    ...options,
    onMutate: async (items, context) => {
      await queryClient.cancelQueries({ queryKey: tagsQueryKey });

      queryClient.setQueryData<Item[]>(tagsQueryKey, (previousTags) =>
        previousTags?.filter((previousTag) => !items.some(({ key }) => key === previousTag.key))
      );

      options?.onMutate?.(items, context);
    },
    onSettled: (responses, error, variables, onMutateResult, context) => {
      invalidateIfAllMutationsDone(tagsQueryKey, [ITEM_QUERY_KEY, jobKey]);

      options?.onSettled?.(responses, error, variables, onMutateResult, context);
    },
  };
};

export const { useScopedMutation: useDeleteItems, useScopedMutationBuilder: useDeleteItemsBuilder } =
  createScopedMutation(getDeleteTagsOptions);

export type StringArrayMap = Record<string, string[]>;
export type StringMap = Record<string, Record<string, string>>;
export type NestedStringArrayMap = Record<string, Record<string, string[]>>;

export type GetLabelPostprocessingResponse = {
  companyLabelSuppression: StringArrayMap;
  companyNotRoomLabelSuppression: NestedStringArrayMap;
  companyRoomLabelSuppression: NestedStringArrayMap;
  labelRemapping: StringArrayMap;
  notRoomLabelRemapping: StringMap;
  roomLabelRemapping: StringMap;
};

export const getLabelPostprocessing = async () => {
  const labelPostprocessing = await queryYemboApiCallWithReturnValue<
    GetLabelPostprocessingResponse,
    'labelPostprocessing'
  >({
    url: `${url}/label-postprocessing`,
    key: 'labelPostprocessing',
    shouldNotifyAndThrowError: true,
  });

  if (!labelPostprocessing) throw new Error('Could not get label postprocessing from API');

  return labelPostprocessing;
};

export const labelPostprocessingQueryOptions: UseQueryOptions<
  GetLabelPostprocessingResponse,
  YemboApiError,
  GetLabelPostprocessingResponse,
  string[]
> = {
  queryKey: [LABEL_POSTPROCESSING_QUERY_KEY],
  queryFn: getLabelPostprocessing,
  staleTime: Infinity,
};

export const useLabelPostprocessingQuery = () => useQuery(labelPostprocessingQueryOptions);
